import { useParams, generatePath } from 'react-router-dom';
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Layout, Button, Dropdown, Menu, Progress, Spin, Alert } from 'antd';
import { DownOutlined, DownloadOutlined, ImportOutlined } from '@ant-design/icons';
import * as Cesium from 'cesium';
import '../FileManager.scss';
import { ApiRoutes } from '../../../routes/routeConstants/apiRoutes';
import axiosInstance from '../../../interceptor/axiosInstance';
import { useFileDownload } from '../../../hooks/useFileDownload';
import useDownload from '../../../hooks/useDownload';
import { useFileUpload } from '../../../hooks/useFileUpload';
import { useCustomFileMap } from '../../../hooks/useCustomFileMap';
import { FileNode } from '../../../types/FileManagerTypes';
import { CreateSpatialPreviewResponse, PreviewStatusEvent } from '../../../types/TemporaryPreview.types';
import { ReuploadModal } from '../components/Modals';
import UploadProgressModal from '../components/UploadProgressModal';
import { useCesiumViewer } from '../../../hooks/useCesiumViewer';
import { onSocketEvent, subscribeRoom } from '../../../services/socket.service';
import { LocalStorageKeys } from '../../../enums/localStorageKeys';
import { useAuth } from '../../../context/AuthContext';
import { AllowedRoles } from '../../../routes/routeConstants/allowedRoles';
import PreviewAttributeTable from './components/PreviewAttributeTable';
import { PreviewViewMode } from '../../../enums/previewViewMode';
import Notification from '../../../shared/components/Notification';
import { NotificationTypes } from '../../../enums/notificationTypes';

const WMS_PATH = import.meta.env.VITE_APP_GEOSERVER_WMS_PATH || '/geoserver/mapaki/wms';

// US-109521: preview map uses a topographic basemap (public Esri service, no token).
const ESRI_WORLD_TOPO_URL = 'https://services.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer';

// Backstop only — `preview:status` socket events drive the UI. Covers a missed
// event or a reconnect gap.
const POLL_INTERVAL_MS = 30000;

// Stable empty seed for useCustomFileMap — a fresh `{}` each render would
// churn the hook's useMemo([rootId, customFileMap]) identity for no reason.
const EMPTY_FILE_MAP = {};

const FileFullscreenPreview: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [preview, setPreview] = useState<CreateSpatialPreviewResponse | null>(null);
  const [fileInfo, setFileInfo] = useState<FileNode>();
  const [viewMode, setViewMode] = useState<PreviewViewMode>(PreviewViewMode.MAP);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadingTip, setLoadingTip] = useState('Validating spatial dataset…');
  const [retryKey, setRetryKey] = useState(0);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [processingMessage, setProcessingMessage] = useState('Queued for spatial processing…');

  const cesiumContainerRef = useRef<HTMLDivElement | null>(null);
  const viewerRef = useRef<Cesium.Viewer | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  const imageryLayerRef = useRef<Cesium.ImageryLayer | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Teardown for the current preview subscription (event listener + room).
  const socketCleanupRef = useRef<(() => void) | null>(null);

  const isShapefileValidationError = (msg: string) => /incomplete shapefile|unable to preview shapefile|required files/i.test(msg);

  const isCogValidationError = (msg: string) => /cloud-optimized geotiff|not a valid.*cog|gdal_translate/i.test(msg);

  const previewReady = Boolean(preview) && (preview?.status === 'ready' || preview?.status == null) && !error;

  const isProcessing = Boolean(preview) && preview?.status === 'processing' && !error;

  const { isViewerReady } = useCesiumViewer({
    container: cesiumContainerRef,
    viewerRef,
    enabled: previewReady,
    recreateKey: previewReady ? preview?.sessionId : undefined,
    viewerOptions: {
      // US-109521: preview is a restricted environment — suppress all stock
      // Cesium chrome (no toolbar, geocoder, scene modes, timeline, infobox).
      animation: false,
      timeline: false,
      fullscreenButton: false,
      infoBox: false,
      selectionIndicator: false,
      homeButton: false,
      geocoder: false,
      sceneModePicker: false,
      navigationHelpButton: false,
      baseLayerPicker: false,
      terrainProvider: undefined,
      // Suppress the default Ion/Bing base; Esri World Topo is added below.
      baseLayer: false,
    },
  });

  const [applyOverlay, setApplyOverlay] = useState(false);
  const showOverlay = () => setApplyOverlay(true);
  const hideOverlay = () => setApplyOverlay(false);

  const [showReuploadModal, setShowReuploadModal] = useState(false);
  const showReupload = () => {
    showOverlay();
    setShowReuploadModal(true);
  };
  const hideReupload = () => {
    hideOverlay();
    setShowReuploadModal(false);
  };

  // Prefer fileMap for the parent-chain walk — folderMap is home-view-only and
  // can lag on this standalone preview page.
  const { updateMapping, fileMap, rootFolderId } = useCustomFileMap(EMPTY_FILE_MAP);
  // Vector export: same async ogr2ogr → S3 path as Mapportal attribute table.
  const { handleAsyncExport, isLayerDownloadInProgress } = useDownload();
  // Raster / original-object download: File Manager bulk download (presigned S3).
  const { handleBulkDownload, isBulkDownloading } = useFileDownload();
  const isDownloadInProgress = isLayerDownloadInProgress || isBulkDownloading;

  // US-109521: Reupload is editor/admin-only; download stays open to every role.
  const { canAccess } = useAuth();
  const canReupload = canAccess({ allowedRoles: AllowedRoles.EDITOR_AND_ABOVE });
  // Same role gate as ViewAttributeTable's edit pencil (LegendDrawer's canEditLayer).
  const canEditTable = canAccess({ allowedRoles: AllowedRoles.EDITOR_AND_ABOVE });

  // S3 id-chain prefix of the file's parent folder — same derivation as
  // VFSBrowser.uploadFolderS3Key (walk fileMap from parent up to root).
  const reuploadFolderId = fileInfo?.parentId || rootFolderId;
  const reuploadFolderS3Key = useMemo(() => {
    if (!rootFolderId) return '';
    if (!reuploadFolderId || reuploadFolderId === rootFolderId) return rootFolderId;
    const chain: string[] = [];
    const seen = new Set<string>();
    let cursor: string | undefined = reuploadFolderId;
    while (cursor && !seen.has(cursor)) {
      seen.add(cursor);
      chain.unshift(cursor);
      cursor = fileMap[cursor]?.parentId;
    }
    if (chain[0] !== rootFolderId) chain.unshift(rootFolderId);
    return chain.join('/');
  }, [reuploadFolderId, rootFolderId, fileMap]);

  // Rebuild the temp preview only after /s3/upload-complete succeeds — not when
  // S3 PUT progress hits 100% (DB replacement + reingest queue still pending then).
  const handleReuploadComplete = useCallback(() => {
    setRetryKey((k) => k + 1);
  }, []);

  const { selectedFiles, reuploadProgress, isUploading, cancelUpload, handleFileChange, handleFileUpload } = useFileUpload(
    reuploadFolderId,
    updateMapping,
    reuploadFolderS3Key,
    handleReuploadComplete,
  );

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const unsubscribeSocket = useCallback(() => {
    try {
      socketCleanupRef.current?.();
    } catch {
      /* ignore */
    }
    socketCleanupRef.current = null;
  }, []);

  const closeSession = useCallback(
    (sessionId: string | null) => {
      if (!sessionId) return;
      stopPolling();
      unsubscribeSocket();
      const url = `${ApiRoutes.API_BASE_URL}${generatePath(ApiRoutes.FILEMANAGER_SPATIAL_PREVIEW_CLOSE, { sessionId })}`;
      // keepalive lets the browser finish the DELETE during pagehide/tab close
      // (sendBeacon is POST-only). Auth mirrors the axiosInstance interceptor;
      // the server-side TTL remains the backstop if the request is dropped.
      const token = localStorage.getItem(LocalStorageKeys.AUTH_TOKEN);
      try {
        void fetch(url, {
          method: 'DELETE',
          keepalive: true,
          credentials: 'include',
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        }).catch(() => undefined);
      } catch {
        /* ignore */
      }
    },
    [stopPolling, unsubscribeSocket],
  );

  const applyReadyPreview = useCallback(
    (data: CreateSpatialPreviewResponse) => {
      stopPolling();
      setPreview({ ...data, status: data.status || 'ready' });
      setProcessingProgress(100);
      setLoadingTip('Loading map…');
      setLoading(false);
      setError(null);
    },
    [stopPolling],
  );

  const applyFailedPreview = useCallback(
    (message: string, data?: CreateSpatialPreviewResponse | null) => {
      stopPolling();
      unsubscribeSocket();
      if (data) setPreview({ ...data, status: 'failed' });
      setError(message || 'Preview failed');
      setLoading(false);
    },
    [stopPolling, unsubscribeSocket],
  );

  const pollSession = useCallback(
    async (sessionId: string) => {
      try {
        const url = generatePath(ApiRoutes.FILEMANAGER_SPATIAL_PREVIEW_SESSION, { sessionId });
        const { data } = await axiosInstance.get<CreateSpatialPreviewResponse>(url);
        if (data.status === 'ready' || data.status == null) {
          applyReadyPreview(data);
        } else if (data.status === 'failed') {
          applyFailedPreview(data.errorMessage || 'Preview failed', data);
        } else {
          setPreview(data);
          setProcessingProgress(data.progress ?? 0);
          setProcessingMessage(
            data.progress != null && data.progress > 0 ? `Processing spatial dataset… ${data.progress}%` : 'Queued for spatial processing…',
          );
        }
      } catch (e: any) {
        // Transient poll errors are ignored; socket or next poll will recover.
        console.warn('[preview-poll]', e?.message || e);
      }
    },
    [applyReadyPreview, applyFailedPreview],
  );

  const startPolling = useCallback(
    (sessionId: string) => {
      stopPolling();
      pollTimerRef.current = setInterval(() => {
        void pollSession(sessionId);
      }, POLL_INTERVAL_MS);
    },
    [pollSession, stopPolling],
  );

  const subscribePreviewStatus = useCallback(
    (sessionId: string) => {
      unsubscribeSocket();
      try {
        const onStatus = (evt: PreviewStatusEvent) => {
          if (!evt || evt.sessionId !== sessionId) return;
          if (evt.status === 'ready' && evt.preview) {
            applyReadyPreview(evt.preview);
            return;
          }
          if (evt.status === 'failed') {
            applyFailedPreview(evt.message || evt.preview?.errorMessage || 'Preview failed', evt.preview || null);
            return;
          }
          // processing
          if (evt.preview) setPreview(evt.preview);
          if (typeof evt.progress === 'number') setProcessingProgress(evt.progress);
          if (evt.message) setProcessingMessage(evt.message);
        };

        const off = onSocketEvent<PreviewStatusEvent>('preview:status', onStatus);
        // subscribeRoom re-joins on reconnect; rooms are per-connection, so a
        // dropped socket would otherwise silently stop delivering events.
        const leaveRoom = subscribeRoom('preview:subscribe', { sessionId }, 'preview:unsubscribe');
        socketCleanupRef.current = () => {
          off();
          leaveRoom();
        };

        // Always poll as a safety net (missed events / reconnect).
        startPolling(sessionId);
      } catch (e) {
        console.warn('[preview-socket] subscribe failed, polling only', e);
        startPolling(sessionId);
      }
    },
    [applyReadyPreview, applyFailedPreview, startPolling, unsubscribeSocket],
  );

  // Create preview session + load file metadata
  useEffect(() => {
    if (!id) return;
    // useParams already decodes; keep a stable fileId for API calls.
    const fileId = id;
    let cancelled = false;

    const run = async () => {
      setLoading(true);
      setError(null);
      setPreview(null);
      setProcessingProgress(0);
      setProcessingMessage('Queued for spatial processing…');
      setLoadingTip('Validating spatial dataset…');
      stopPolling();
      unsubscribeSocket();
      try {
        // Close any previous session before retrying.
        if (sessionIdRef.current) {
          closeSession(sessionIdRef.current);
          sessionIdRef.current = null;
        }

        setLoadingTip('Creating temporary preview map…');
        const [previewRes, fileRes] = await Promise.allSettled([
          axiosInstance.post(
            // Express :fileId is a single segment; ids can contain "/" (folderId/fileName).
            generatePath(ApiRoutes.FILEMANAGER_SPATIAL_PREVIEW, {
              fileId: encodeURIComponent(fileId),
            }),
          ),
          // Express :id is a single segment; encode so dots/special chars stay intact.
          axiosInstance.get(`/filemanager/file/${encodeURIComponent(fileId)}`),
        ]);

        if (cancelled) return;

        if (previewRes.status === 'fulfilled') {
          const data = previewRes.value.data as CreateSpatialPreviewResponse;
          sessionIdRef.current = data.sessionId;

          if (data.status === 'processing') {
            setPreview(data);
            setProcessingProgress(data.progress ?? 0);
            setProcessingMessage('Queued for spatial processing…');
            setLoading(false);
            subscribePreviewStatus(data.sessionId);
          } else if (data.status === 'failed') {
            applyFailedPreview(data.errorMessage || 'Preview failed', data);
          } else {
            // ready (or legacy response without status)
            applyReadyPreview(data);
          }
        } else {
          const msg =
            (previewRes.reason as any)?.response?.data?.message || (previewRes.reason as any)?.message || 'Failed to create spatial preview';
          setError(String(msg));
          setLoading(false);
        }

        if (fileRes.status === 'fulfilled') {
          setFileInfo(fileRes.value.data.file);
        }
      } catch (e: any) {
        if (!cancelled) {
          setError(e?.message || 'Failed to load preview');
          setLoading(false);
        }
      }
    };

    void run();

    return () => {
      cancelled = true;
      const sid = sessionIdRef.current;
      sessionIdRef.current = null;
      closeSession(sid);
    };
  }, [id, closeSession, retryKey, stopPolling, unsubscribeSocket, subscribePreviewStatus, applyReadyPreview, applyFailedPreview]);

  // NOTE: deliberately no disconnectSocket() here. The socket is now shared with
  // layer-publish, export and download status, so tearing it down on this page's
  // unmount would cut those off. It's disconnected on logout instead.

  // Destroy the temp preview only on a real document teardown.
  // Do NOT listen to `beforeunload` here: some browsers fire it around
  // cross-origin attachment downloads, which would DROP the live `preview_*`
  // PostGIS table while the user is still on this page (first export OK,
  // second fails with "relation does not exist"). React effect cleanup on
  // unmount still closes the session when leaving the route.
  useEffect(() => {
    const onPageHide = (e: PageTransitionEvent) => {
      // Entering bfcache — page (and table) may come back; leave session alive.
      if (e.persisted) return;
      closeSession(sessionIdRef.current);
    };
    window.addEventListener('pagehide', onPageHide);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
    };
  }, [closeSession]);

  // Esri World Topographic basemap (baseLayer: false suppressed the Ion default).
  // Separate effect from the WMS overlay so overlay re-runs don't churn the basemap.
  useEffect(() => {
    if (!isViewerReady || !viewerRef.current) return;
    const viewer = viewerRef.current;
    const baseLayer = Cesium.ImageryLayer.fromProviderAsync(Cesium.ArcGisMapServerImageryProvider.fromUrl(ESRI_WORLD_TOPO_URL), {});
    viewer.imageryLayers.add(baseLayer, 0); // index 0 = under the WMS overlay
    return () => {
      if (!viewer.isDestroyed?.()) {
        try {
          viewer.imageryLayers.remove(baseLayer, true);
        } catch {
          /* ignore */
        }
      }
    };
  }, [isViewerReady]);

  // Attach WMS imagery when viewer + session are ready
  useEffect(() => {
    if (!previewReady || !preview || !isViewerReady || !viewerRef.current) return;
    const viewer = viewerRef.current;

    // Remove previous imagery if any
    if (imageryLayerRef.current) {
      try {
        viewer.imageryLayers.remove(imageryLayerRef.current, true);
      } catch {
        /* ignore */
      }
      imageryLayerRef.current = null;
    }

    const layers = `${preview.workspace}:${preview.layerName}`;
    const wmsUrl = `${ApiRoutes.GEOSERVER_BASE_URL}${preview.wmsUrl || WMS_PATH}`;

    const provider = new Cesium.WebMapServiceImageryProvider({
      url: wmsUrl,
      layers,
      parameters: {
        service: 'WMS',
        version: '1.1.1',
        request: 'GetMap',
        transparent: true,
        format: 'image/png',
        tiled: true,
      },
      tileWidth: 512,
      tileHeight: 512,
    });

    const imageryLayer = viewer.imageryLayers.addImageryProvider(provider);
    imageryLayerRef.current = imageryLayer;

    // Fly to bounds when available
    const b = preview.bounds;
    if (b && (b.minX !== b.maxX || b.minY !== b.maxY)) {
      const rectangle = Cesium.Rectangle.fromDegrees(b.minX, b.minY, b.maxX, b.maxY);
      viewer.camera.flyTo({ destination: rectangle, duration: 1.2 });
    }

    return () => {
      if (imageryLayerRef.current && viewer && !viewer.isDestroyed?.()) {
        try {
          viewer.imageryLayers.remove(imageryLayerRef.current, true);
        } catch {
          /* ignore */
        }
        imageryLayerRef.current = null;
      }
    };
  }, [preview, previewReady, isViewerReady]);

  const isRaster = preview?.layerType === 'raster';

  // Rasters have no attribute table — force the map view if the mode lingers.
  useEffect(() => {
    if (isRaster && viewMode === PreviewViewMode.TABLE) setViewMode(PreviewViewMode.MAP);
  }, [isRaster, viewMode]);

  // Vector formats go through the Mapportal async export path (PostGIS → S3).
  // Rasters have no PostGIS table — download the original File Manager object.
  const handleDownload = (format: string) => {
    if (isDownloadInProgress) return;

    if (isRaster || format === 'geotiff') {
      if (!fileInfo) {
        Notification({
          message: 'File details are still loading. Try again in a moment.',
          type: NotificationTypes.WARNING,
        });
        return;
      }
      void handleBulkDownload([fileInfo]);
      return;
    }

    const layerName = preview?.layerName;
    if (!layerName) {
      Notification({
        message: 'Preview layer is not ready to export yet.',
        type: NotificationTypes.WARNING,
      });
      return;
    }

    const exportFormat = format as 'shapefile' | 'csv' | 'kml' | 'geojson';
    void handleAsyncExport(layerName, exportFormat);
  };

  const handleDownloadMenuClick = (e: { key: string }) => handleDownload(e.key);
  const handleViewChange = (e: { key: string }) => setViewMode(e.key as PreviewViewMode);
  const handleCloseTable = () => setViewMode(PreviewViewMode.MAP);
  const handleRetry = () => setRetryKey((k) => k + 1);

  const downloadMenu = (
    <Menu
      onClick={handleDownloadMenuClick}
      disabled={isDownloadInProgress}
      items={
        isRaster
          ? [{ label: 'DOWNLOAD GEOTIFF', key: 'geotiff' }]
          : [
              { label: 'DOWNLOAD SHP', key: 'shapefile' },
              { label: 'DOWNLOAD KML', key: 'kml' },
              { label: 'DOWNLOAD TABLE', key: 'csv' },
            ]
      }
    />
  );

  // MAP VIEW / TABLE VIEW selector — Table View is vector-only (rasters have no
  // attribute table).
  const viewMenu = (
    <Menu
      onClick={handleViewChange}
      items={[{ label: 'MAP VIEW', key: PreviewViewMode.MAP }, ...(isRaster ? [] : [{ label: 'TABLE VIEW', key: PreviewViewMode.TABLE }])]}
    />
  );

  const previewTitle = (preview?.fileName || fileInfo?.name || 'Spatial Preview').toUpperCase();

  return (
    <Layout className="file-fullscreen-preview">
      <Layout.Content className="file-fullscreen-preview__content">
        {applyOverlay && <div className="overlay"></div>}
        {fileInfo && (
          <>
            <ReuploadModal
              show={showReuploadModal}
              hide={hideReupload}
              selectedFiles={selectedFiles}
              reuploadFiles={[fileInfo]}
              handleFileChange={handleFileChange}
              handleFileUpload={handleFileUpload}
              isUploading={isUploading}
            />
          </>
        )}

        {loading && (
          <div className="preview-loading">
            <Spin size="large" tip={loadingTip} />
          </div>
        )}

        {!loading && isProcessing && (
          <div className="preview-processing">
            <Spin size="large" />
            <div className="preview-processing__body">
              <h3 className="preview-processing__title">Building spatial preview</h3>
              <p className="preview-processing__message">{processingMessage}</p>
              <Progress className="preview-processing__bar" percent={processingProgress} status="active" />
              <p className="preview-processing__hint">
                Large shapefiles are processed in the background so the request never times out. This page updates automatically when the map is
                ready.
              </p>
            </div>
          </div>
        )}

        {!loading && error && (
          <Alert
            className="preview-error"
            type="error"
            showIcon
            message="Preview unavailable"
            description={
              <div>
                <p className="preview-error__line">{error}</p>
                {isShapefileValidationError(error) && (
                  <p className="preview-error__line preview-error__hint">
                    Upload the missing shapefile components (.shp, .shx, .dbf, .prj) into the same folder with the same base filename, then try again.
                  </p>
                )}
                {isCogValidationError(error) && (
                  <p className="preview-error__line preview-error__hint">
                    GeoTIFF preview requires a Cloud-Optimized GeoTIFF (COG). Re-encode the file and re-upload, then try again.
                  </p>
                )}
                <Button type="primary" onClick={handleRetry}>
                  Retry preview
                </Button>
              </div>
            }
          />
        )}

        {!loading && !error && previewReady && preview && (
          <div className="preview-ready">
            <h2 className="preview-title">{previewTitle}</h2>

            <div className="preview-controls">
              <Dropdown overlay={viewMenu} trigger={['click']}>
                <Button className="filter__button preview-btn">
                  {viewMode === PreviewViewMode.TABLE ? 'TABLE VIEW' : 'MAP VIEW'}
                  <DownOutlined className="preview-btn__caret" />
                </Button>
              </Dropdown>

              <div className="preview-controls__actions">
                <Dropdown overlay={downloadMenu} trigger={['click']} disabled={isDownloadInProgress}>
                  <Button className="filter__button preview-btn" loading={isDownloadInProgress} disabled={isDownloadInProgress}>
                    <DownloadOutlined /> DOWNLOAD
                  </Button>
                </Dropdown>
                {canReupload && (
                  <Button className="filter__button preview-btn" onClick={showReupload}>
                    <ImportOutlined /> REUPLOAD
                  </Button>
                )}
              </div>
            </div>

            <div className="preview-map-area">
              <div ref={cesiumContainerRef} className="cesium-container" />
              {viewMode === PreviewViewMode.TABLE && !isRaster && (
                <PreviewAttributeTable
                  sessionId={preview.sessionId}
                  title={previewTitle}
                  onClose={handleCloseTable}
                  editable={!preview.isTemporary && canEditTable}
                />
              )}
            </div>
          </div>
        )}

        <UploadProgressModal uploadProgress={reuploadProgress} onCancel={cancelUpload} />
      </Layout.Content>
    </Layout>
  );
};

export default FileFullscreenPreview;
